import React, { useEffect, useState, useRef } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../Auth/firebase";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { useProjects } from "../../context/ProjectsContext";

const MyTasks = ({ userId }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [visibleTasks, setVisibleTasks] = useState(3);
  const cachedTasks = useRef([]);
  const navigate = useNavigate();
  const { activeProject } = useProjects();

  const fetchTasks = (projectId) => {
    const tasksArray = [];
    const boardsCollectionRef = collection(db, `projects/${projectId}/boards`);

    onSnapshot(boardsCollectionRef, (boardDocs) => {
      tasksArray.length = 0; // Clear previous data

      boardDocs.forEach((boardDoc) => {
        const boardId = boardDoc.id;
        const boardName = boardDoc.data().name;

        const tasksCollectionRef = collection(
          db,
          `projects/${projectId}/boards/${boardId}/tasks`
        );

        onSnapshot(tasksCollectionRef, (tasksSnapshot) => {
          tasksSnapshot.forEach((taskDoc) => {
            const taskData = taskDoc.data();
            tasksArray.push({
              ...taskData,
              id: taskDoc.id,
              boardName: boardName,
              boardId: boardId,
            });
          });

          cachedTasks.current = tasksArray;
          setTasks(tasksArray);
          setLoading(false);
        });
      });
    });
  };

  useEffect(() => {
    if (activeProject) {
      fetchTasks(activeProject.id);
    }
  }, [activeProject]);

  const handleTaskClick = (boardId) => {
    navigate(`/projects/${activeProject.id}/boards/${boardId}`);
  };

  const filteredTasks = tasks
    .filter((task) =>
      task.title.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const aDate = a.dueDate ? a.dueDate.toDate().getTime() : Infinity;
      const bDate = b.dueDate ? b.dueDate.toDate().getTime() : Infinity;
      if (a.completed && !b.completed) return 1;
      if (!a.completed && b.completed) return -1;
      return aDate - bDate;
    });

  const handleShowMore = () => {
    setVisibleTasks((prevVisibleTasks) => prevVisibleTasks + 3);
  };

  if (loading) {
    return (
      <div className="flex flex-col p-4 bg-white shadow-lg rounded-lg h-full w-[50%] gap-2">
        <div className="w-full h-4 bg-gray-200 animate-pulse rounded-lg"></div>
        <div className="w-full h-4 bg-gray-200 animate-pulse rounded-lg"></div>
        <div className="w-full h-4 bg-gray-200 animate-pulse rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col p-4 bg-white shadow-lg rounded-lg h-full w-[50%]">
      <h2 className="text-2xl font-semibold mb-4">My Tasks</h2>
      <input
        type="text"
        placeholder="Search tasks..."
        className="p-2 border border-gray-300 rounded-lg mb-4 w-full outline-none focus:ring-2 focus:ring-blue-500"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
      />
      <div className="flex flex-col gap-2 overflow-y-auto">
        {filteredTasks.slice(0, visibleTasks).map((task) => (
          <div
            key={task.id}
            className="flex justify-between items-center py-2 px-4 bg-gray-50 shadow-sm rounded-lg cursor-pointer hover:shadow-md transition-shadow duration-200"
            onClick={() => handleTaskClick(task.boardId)}
          >
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                className="form-checkbox h-5 w-5 text-blue-600 cursor-pointer"
                checked={task.completed || false}
                readOnly
              />
              <span className="text-lg font-semibold">{task.title}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="py-1 px-2 rounded-full bg-indigo-200 text-indigo-700 text-xs font-medium">
                {task.boardName}
              </span>
              <span className="text-sm text-gray-600">
                {task.dueDate
                  ? format(task.dueDate.toDate(), "yyyy-MM-dd")
                  : "No due date"}
              </span>
            </div>
          </div>
        ))}
      </div>
      {filteredTasks.length > visibleTasks && (
        <button
          onClick={handleShowMore}
          className="mt-4 py-2 bg-indigo-400 text-white rounded-lg hover:bg-indigo-500 transition-colors duration-200"
        >
          Show More
        </button>
      )}
    </div>
  );
};

export default MyTasks;
